import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface AssignmentFiltersState {
  search: string;
  regions: string[];
  status: string | undefined;
  source: string | undefined;
}

const DEFAULT_FILTERS: AssignmentFiltersState = {
  search: '',
  regions: [],
  status: 'active',
  source: undefined
};

const STORAGE_KEY = 'eligaro-assignment-filters';

interface PreferencesShape {
  assignmentFilters?: AssignmentFiltersState;
}

function loadLocal(): AssignmentFiltersState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<AssignmentFiltersState>;
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function saveLocal(filters: AssignmentFiltersState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore quota errors
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function saveToServer(filters: AssignmentFiltersState): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    apiFetch('/preferences', {
      method: 'PUT',
      body: JSON.stringify({ assignmentFilters: filters })
    }).catch(() => {
      // Silent fail — localStorage is the fallback
    });
  }, 500);
}

/**
 * Assignment filter state persisted to localStorage (immediate) and the
 * /preferences endpoint (debounced). On mount we fetch the server copy and,
 * if present, prefer it over localStorage so settings follow the user across
 * devices.
 */
export function useAssignmentFilters() {
  const [filters, setFilters] = useState<AssignmentFiltersState>(loadLocal);
  const hydratedFromServer = useRef(false);

  const { data: prefsResponse } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => apiFetch<PreferencesShape>('/preferences'),
    staleTime: 5 * 60_000
  });

  useEffect(() => {
    if (hydratedFromServer.current) return;
    const serverFilters = prefsResponse?.data?.assignmentFilters;
    if (serverFilters) {
      hydratedFromServer.current = true;
      setFilters({ ...DEFAULT_FILTERS, ...serverFilters });
    } else if (prefsResponse?.success) {
      hydratedFromServer.current = true;
    }
  }, [prefsResponse]);

  function update(patch: Partial<AssignmentFiltersState>) {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      saveLocal(next);
      saveToServer(next);
      return next;
    });
  }

  return { filters, update };
}
